import React from "react";
import { TextField, Paper, Typography, Box, Link, LinearProgress, Button, InputAdornment } from "@mui/material";
import { schemas, useForm } from "helpers/FormHelpers";
import { Checkmark } from "components/Icons";
import { P } from "theme/typography";
import { BackendResponse, backend } from "Backend";
import theme from "theme";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";

const schema = {
  email: schemas.email,
};
type Field = "email";

const classes = {
  root: {
    backgroundColor: theme.palette.background.default,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    width: "100%",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    width: "400px",
    backgroundColor: "white",
    padding: theme.spacing(2, 3),
    marginBottom: theme.spacing(1),
  },
  form: {
    width: "400px",
  },
  textField: {
    minHeight: "80px",
  },
  title: {
    marginBottom: theme.spacing(2),
    width: "400px",
  },
  button: {
    height: "3rem",
  },
  barContainer: {
    minHeight: "30px",
  },
  helpBar: {
    display: "flex",
    justifyContent: "space-between",
  },
  link: {
    ...theme.mixins.linkLike,
  },
};

const ForgotPassword = () => {
  const {
    formStateHandle: [formState, _setFormState],
    isGood,
    hasError,
    errorText,
    handleChange,
  } = useForm<Field>(
    {
      isValid: false,
      values: { email: "" },
      touched: { email: false },
      errors: {},
    },
    schema
  );

  const [submitError, setSubmitError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const [isSuccess, setSuccess] = React.useState(false);

  const { searchParams } = useSearchParamsWithUpdate();

  const handleSubmit: React.FormEventHandler = async (event) => {
    event.preventDefault();
    setLoading(true);
    setSubmitError("");

    let result: BackendResponse<void>;

    if (searchParams.get("person") === "true") {
      result = await backend.forgotPersonPassword(formState.values["email"].toLowerCase().trim());
    } else {
      result = await backend.forgotPassword(formState.values["email"].toLowerCase().trim());
    }
    switch (result.tag) {
      case "success": {
        setSuccess(true);
        break;
      }
      case "error": {
        setSuccess(false);
        setSubmitError("Something went wrong. Please try again later.");
        break;
      }
      default: {
        setSuccess(true);
        break;
      }
    }
    setLoading(false);
  };

  return (
    <Box sx={classes.root}>
      <P sx={classes.title} variant="h5" align="center">
        We&apos;ll send you an email so you can reset your password
      </P>
      <form style={classes.form} onSubmit={handleSubmit}>
        <Paper sx={classes.container}>
          <TextField
            id="email"
            label="Email"
            name="email"
            size="medium"
            sx={classes.textField}
            autoFocus
            fullWidth
            margin="normal"
            error={hasError("email")}
            helperText={errorText("email")}
            onChange={handleChange}
            value={formState.values.email || ""}
            InputProps={{
              endAdornment: <InputAdornment position="end">{isGood("email") && <Checkmark />}</InputAdornment>,
            }}
          />
          <Button
            color="primary"
            disabled={!formState.isValid}
            fullWidth
            size="large"
            type="submit"
            variant="contained">
            Submit
          </Button>
          <Box sx={classes.barContainer}>
            {submitError && (
              <Typography color="error" align="center" variant="caption">
                {submitError}
              </Typography>
            )}
            <Box visibility={loading ? "visible" : "hidden"}>
              <LinearProgress />
            </Box>
          </Box>
          {isSuccess && <Typography>Thanks! Please check your email for password reset instructions</Typography>}
        </Paper>
        <Box sx={classes.helpBar}>
          <Link href="mailto:matt@patterndataworks.com" sx={classes.link} variant="caption">
            Help
          </Link>
        </Box>
      </form>
    </Box>
  );
};
export default ForgotPassword;
